import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/Urls";
import { CLOUDINARY_PURPOSES, uploadDirectToCloudinary } from "../lib/cloudinaryUpload";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ImageUploader from "../components/ImageUploader";

const AVAILABILITY_MULTIPLIERS = {
  hours: 1,
  days: 24,
  months: 24 * 30,
  years: 24 * 365,
};

const splitAvailability = (storedHours) => {
  const hours = Number(storedHours);

  if (!Number.isFinite(hours) || hours <= 0) {
    return {
      value: "",
      unit: "hours",
    };
  }

  const units = ["years", "months", "days"];

  for (const unit of units) {
    const multiplier = AVAILABILITY_MULTIPLIERS[unit];

    if (hours % multiplier === 0) {
      return {
        value: String(hours / multiplier),
        unit,
      };
    }
  }

  return {
    value: String(hours),
    unit: "hours",
  };
};

const EditItem = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState("");
  const [availabilityUnit, setAvailabilityUnit] = useState("hours");

  const [form, setForm] = useState({
    itemName: "",
    description: "",
    locCountry: "",
    locCity: "",
    store: "",
    amount: "",
    currency: "",
    type: "",
    availPeriod: "",
    categoryName: "",
  });

  useEffect(() => {
    fetchItem();
  }, []);

  const fetchItem = async () => {
    try {
      const res = await api.get(`/api/category-items/${itemId}`);

      const availability = splitAvailability(res.data.availPeriod);

      setForm({
        itemName: res.data.itemName || "",
        description: res.data.description || "",
        locCountry: res.data.locCountry || "",
        locCity: res.data.locCity || "",
        store: res.data.store || "",
        amount: res.data.amount || "",
        currency: res.data.currency || "",
        type: res.data.type || "",
        availPeriod: availability.value,
        categoryName: res.data.categoryName || "",
      });

      setAvailabilityUnit(availability.unit);
      setPreview(res.data.imageUrl || res.data.image || "");
    } catch (err) {
      console.error(err);
      alert("Failed to load item");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);

      let imageUrl = null;

      if (image) {
        const uploaded = await uploadDirectToCloudinary(
          image,
          CLOUDINARY_PURPOSES.ITEM_IMAGE
        );
        imageUrl = uploaded.secureUrl;
      }

      const formData = new FormData();

      Object.keys(form).forEach((key) => {
        if (!["availPeriod", "locCountry", "locCity"].includes(key)) {
          formData.append(key, form[key]);
        }
      });

      const availabilityValue = Number(form.availPeriod);
      const availabilityHours =
        availabilityValue * AVAILABILITY_MULTIPLIERS[availabilityUnit];

      formData.append("availPeriod", String(availabilityHours));

      if (imageUrl) {
        formData.append("imageUrl", imageUrl);
      }

      await api.put(`/api/category-items/${itemId}`, formData);

      alert("Item updated successfully!");
      navigate("/profile");
    } catch (err) {
      console.error(err);
      alert(
        err?.response?.data?.message ||
          err?.response?.data ||
          "Failed to update item"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
        <div className="min-h-screen bg-background flex flex-col animate-pulse">

        {/* NAVBAR SKELETON */}
        <div className="h-16 border-b border-border bg-card" />

        {/* CONTENT */}
        <div className="flex-1 flex items-center justify-center px-4 pt-28 pb-10">

            <div className="w-full max-w-3xl rounded-2xl border border-border bg-card p-8 shadow-xl">

            {/* HEADER */}
            <div className="mb-8 space-y-3">
                <div className="h-8 w-48 rounded-md bg-secondary" />
                <div className="h-4 w-72 rounded-md bg-secondary" />
            </div>

            {/* IMAGE */}
            <div className="mb-6">
                <div className="h-64 w-full rounded-xl bg-secondary" />
            </div>

            {/* INPUTS */}
            <div className="space-y-5">

                <div className="h-12 w-full rounded-lg bg-secondary" />

                <div className="h-32 w-full rounded-lg bg-secondary" />

                <div className="h-12 w-full rounded-lg bg-secondary" />

                <div className="h-12 w-full rounded-lg bg-secondary" />

                <div className="h-12 w-full rounded-lg bg-secondary" />

                {/* GRID */}
                <div className="grid grid-cols-2 gap-3">
                <div className="h-12 rounded-lg bg-secondary" />
                <div className="h-12 rounded-lg bg-secondary" />
                </div>

                <div className="h-12 w-full rounded-lg bg-secondary" />

                {/* BUTTONS */}
                <div className="flex gap-3 pt-4">
                <div className="h-12 flex-1 rounded-xl bg-secondary" />
                <div className="h-12 w-32 rounded-xl bg-secondary" />
                </div>

            </div>
            </div>
        </div>
        </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />

      <div className="flex-1 flex items-center justify-center px-4 pt-4 pb-10">
        <div className="w-full max-w-3xl bg-card text-card-foreground rounded-2xl p-8 border border-border shadow-xl">
          <div className="mb-6">
            <h2 className="text-3xl font-heading font-bold text-foreground">
              Edit Item <span className="text-primary">Details</span>
            </h2>

            <p className="text-muted-foreground mt-2">
              Update your item details and image.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* IMAGE */}
            <div className="space-y-3">
              <label className="text-sm font-medium leading-none">
                Current Item Image
              </label>

              {preview && !image && (
                <div className="overflow-hidden rounded-xl border border-border">
                  <img
                    src={preview}
                    alt="Current Item"
                    className="h-64 w-full object-cover"
                  />
                </div>
              )}

              {image && (
                <div className="overflow-hidden rounded-xl border border-border">
                  <img
                    src={URL.createObjectURL(image)}
                    alt="New Preview"
                    className="h-64 w-full object-cover"
                  />
                </div>
              )}

              <ImageUploader onChange={setImage} />
            </div>

            <input
              name="itemName"
              value={form.itemName}
              onChange={handleChange}
              placeholder="Item Name"
              className="w-full rounded-lg border border-border bg-secondary px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              required
            />

            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Description"
              rows="4"
              className="w-full rounded-lg border border-border bg-secondary px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              required
            />

            <input
              name="store"
              value={form.store}
              onChange={handleChange}
              placeholder="Pickup Point"
              className="w-full rounded-lg border border-border bg-secondary px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              required
            />

            <div className="space-y-2">
              <label className="text-sm font-medium leading-none">
                Availability
              </label>

              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  min="1"
                  step="1"
                  name="availPeriod"
                  value={form.availPeriod}
                  onChange={handleChange}
                  placeholder="Value"
                  className="w-full rounded-lg border border-border bg-secondary px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  required
                />

                <select
                  value={availabilityUnit}
                  onChange={(e) => setAvailabilityUnit(e.target.value)}
                  className="w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  aria-label="Availability unit"
                >
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                  <option value="months">Months</option>
                  <option value="years">Years</option>
                </select>
              </div>

              <p className="text-xs text-muted-foreground">
                Choose how long the item should remain available.
              </p>
            </div>

            <select
              name="type"
              value={form.type}
              onChange={handleChange}
              className="w-full rounded-lg border border-border bg-secondary px-4 py-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              required
            >
              <option value="">Select Type</option>
              <option value="seller">seller</option>
              <option value="donator">donator</option>
              <option value="giveaway">giveaway</option>
            </select>

            <div className="grid grid-cols-2 gap-2">
              <select
                name="currency"
                value={form.currency}
                onChange={handleChange}
                className="rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                required
              >
                <option value="">Pick Currency</option>
                <option value="USD">USD</option>
                <option value="UGX">UGX</option>
                <option value="KES">KES</option>
                <option value="TZS">TZS</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>

              <input
                type="number"
                name="amount"
                value={form.amount}
                onChange={handleChange}
                placeholder="Amount"
                className="rounded-lg border border-border bg-secondary px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                required
              />
            </div>

            <input
              name="categoryName"
              value={form.categoryName}
              readOnly
              className="w-full rounded-lg border border-border bg-secondary px-4 py-2.5 text-sm text-foreground opacity-70 cursor-not-allowed"
              required
            />

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 bg-primary text-primary-foreground py-3 rounded-xl font-semibold hover:opacity-90 transition shadow-[var(--glow-primary)] disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>

              <button
                type="button"
                onClick={() => navigate(-1)}
                className="rounded-xl border border-border px-5 py-3 hover:bg-white/5 transition"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default EditItem;
